import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FeatureId } from '@sequesto-os/shared';
import { useAccess } from '../../../hooks/useAccess';
import { SegmentedControl, type TabItem } from '../../../components/ui/Tabs';
import { DemoBookings } from '../bookings/BookingsPage';
import { ProspectMeetings } from '../../ProjectManagement/Sales/MeetingsTab';
import { SdrMeetings } from '../../ProjectManagement/SDR/OverviewTab';
import { DEFAULT_MEETINGS_PERIOD, MEETINGS_PERIODS, isMeetingsPeriodId, meetingsPeriod, type MeetingsPeriodId } from './period';

type View = 'bookings' | 'prospects' | 'sdr';

const VIEWS: readonly (TabItem<View> & { feature: FeatureId })[] = [
  { id: 'bookings', label: 'Demo bookings', feature: 'sales.bookings' },
  { id: 'prospects', label: 'Prospect meetings', feature: 'sales.ae' },
  { id: 'sdr', label: 'SDR meetings', feature: 'sales.sdr' },
];

/**
 * Sales → Meetings: demo bookings (Calendly), the AEs' prospect meetings
 * (Attio calendars) and SDR meetings booked (Fillout). One view at a time,
 * picked with a toggle and kept in `?view=`; one period for all three, kept in
 * `?period=`, so switching views keeps the window.
 */
export function MeetingsPage() {
  const { canAccess } = useAccess();
  const [params, setParams] = useSearchParams();
  const views = VIEWS.filter((v) => canAccess(v.feature));
  const requested = params.get('view');
  const view = views.find((v) => v.id === requested)?.id ?? views[0]?.id;
  const requestedPeriod = params.get('period');
  const periodId: MeetingsPeriodId = isMeetingsPeriodId(requestedPeriod) ? requestedPeriod : DEFAULT_MEETINGS_PERIOD;
  // Recomputed only when the period changes, so the queries keep a stable key.
  const period = useMemo(() => meetingsPeriod(periodId), [periodId]);

  if (!view) return null;

  const setParam = (key: string, value: string) => {
    const search = new URLSearchParams(params);
    search.set(key, value);
    setParams(search, { replace: true });
  };

  const switcher =
    views.length > 1 ? (
      <SegmentedControl options={views.map(({ id, label }) => ({ id, label }))} value={view} onChange={(v) => setParam('view', v)} ariaLabel="Meetings view" />
    ) : undefined;
  const periodControl = (
    <SegmentedControl options={MEETINGS_PERIODS} value={periodId} onChange={(p) => setParam('period', p)} ariaLabel="Period" />
  );

  const props = { leading: switcher, period, periodControl };
  if (view === 'bookings') return <DemoBookings {...props} />;
  if (view === 'prospects') return <ProspectMeetings {...props} />;
  return <SdrMeetings {...props} />;
}
